import { Fragment, useState } from 'react';
import type { CSSProperties } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DeleteButton, Empty, ErrorBanner, Field, Loading, Modal } from '../../components/ui';
import { Segmented } from '../../components/data';
import { api } from '../../lib/api';
import { downloadCsv, fileStamp, slugForFile } from '../../lib/download';
import { formatDate, formatNumber } from '../../lib/format';
import { useResource } from '../../lib/hooks';
import type {
  AmClientRow,
  AmSowArea,
  AmSowDetail,
  AmSowLibraryTask,
  AmSowRow,
  AmSowStatus,
  AmSowTemplateDetail,
  AmSowTemplateRow,
} from '../../lib/types';
import cmitLogo from '../../assets/cmit-logo.png';
import { AmProductPicker, productTotals, type PickedProduct } from './AmProductPicker';
import { StateBadge } from './amUi';
import { AmSowTasks } from './AmSowTasks';

/**
 * Scope of work builder.
 *
 * Five fixed work areas, each with its own boilerplate. The boilerplate is
 * shown read-only and lands in the document verbatim: the value of a standard
 * scope is that every client gets the same definition of "project management",
 * so letting it be edited per engagement would defeat the purpose. Only the
 * tasks and hours vary.
 *
 * The source app generated a PDF with a client-side PDF library. This prints
 * instead — the print stylesheet already strips the app chrome, and a browser's
 * print-to-PDF produces selectable text and real page breaks, which a canvas
 * render does not.
 *
 * A SCOPE IS SAVED AGAINST A CLIENT, which is why this opens on the list of
 * saved ones rather than on a blank form. A scope is never a document about
 * nobody — it is always what we proposed to do for somebody — and storing it
 * anywhere other than on that client would make "what did we scope for them in
 * March" a question about somebody's downloads folder.
 */
const AREAS = [
  {
    key: 'project_management',
    name: 'Project management',
    label: 'Project mgmt',
    description:
      'Project management encompasses the planning, coordination and oversight of all activities required to deliver the engagement on time and within scope. This includes scheduling and facilitating kickoff and status meetings, tracking milestones and deliverables, managing risks and issues, maintaining clear communication between stakeholders, and ensuring that all work aligns with the agreed objectives and timeline.',
  },
  {
    key: 'remote_setup',
    name: 'Remote setup',
    label: 'Remote setup',
    description:
      'Remote setup covers all configuration, installation and deployment activities performed off-site prior to or during the engagement. This includes provisioning systems and software remotely, configuring network and cloud services, establishing secure remote access, and validating that all remote environments are properly set up and tested before onsite or go-live activities begin.',
  },
  {
    key: 'onsite_setup',
    name: 'Onsite setup',
    label: 'Onsite setup',
    description:
      "Onsite setup involves all hands-on installation, configuration and deployment activities performed at the client's physical location. This includes racking and stacking hardware, running cables, configuring on-premises equipment, integrating systems with the existing environment, and performing on-the-ground testing and validation to ensure everything is operational before handover.",
  },
  {
    key: 'documentation',
    name: 'Documentation',
    label: 'Documentation',
    description:
      "Documentation covers the creation, organisation and delivery of all written material that supports the engagement and its outcomes. This includes network diagrams, configuration records, standard operating procedures, runbooks, user guides and as-built documentation. Thorough documentation ensures the client's team has the knowledge and reference material needed to operate, maintain and troubleshoot the solution long after the engagement concludes.",
  },
  {
    key: 'training',
    name: 'Training and support',
    label: 'Training',
    description:
      "Training and support encompasses all activities focused on enabling the client's team to confidently use and manage the delivered solution. This includes hands-on training sessions, walkthroughs of key workflows, knowledge transfer to administrators and end users, and post-deployment support to address questions or issues that arise during the transition period. The goal is a smooth handover and long-term success for the client.",
  },
] as const;

/** The same five, for anything outside the builder that has to group by them —
 *  the task library screen sorts its groups into this order so the library
 *  reads down the page in the order a scope is written. */
export const SOW_AREAS: readonly { key: string; name: string }[] = AREAS;

type AreaKey = (typeof AREAS)[number]['key'];

interface Task {
  /**
   * Local to this editing session and never sent anywhere — `save()` maps each
   * row to name/description/hours explicitly, and the stored document is an
   * ordered array with no ids in it.
   *
   * It exists for React's key. Index keys were fine while rows only ever
   * appended, but a reorder moves values between positions: with index keys
   * React keeps the DOM node and swaps the text underneath it, so a focused
   * input silently becomes a different task's input. A stable key makes React
   * move the node instead, and focus travels with the row somebody is dragging.
   */
  id: string;
  name: string;
  description: string;
  hours: string;
}

/* Monotonic per page load. Nothing persists it and nothing compares two of
   them across sessions — it only has to be unique among the rows on screen. */
let taskSeq = 0;
const blankTask = (): Task => ({ id: `task-${++taskSeq}`, name: '', description: '', hours: '' });

/* Products sit between the last work area and the review, because that is the
   order the question actually arrives in: you scope the work, then you know
   what it takes to do it. */
const STEPS = ['Details', ...AREAS.map((a) => a.label), 'Products', 'Review'];
const PRODUCTS_STEP = STEPS.length - 1;

/** The dropdown's escape hatch: a scope for a company that is not on the books
 *  yet. It can be written and printed, but not saved — see `canSave`. */
const OFF_BOOK = '__other__';

/** Where a scope has got to with the client. Mirrors the CHECK constraint on
 *  coo.AmSow; the server is the one that enforces it. */
const SOW_STATUSES: AmSowStatus[] = ['New', 'Needs approval', 'Approved', 'Closed'];

const hoursOf = (task: Task) => Number.parseFloat(task.hours) || 0;
const areaHours = (tasks: Task[]) => tasks.reduce((sum, t) => sum + hoursOf(t), 0);

/** Only rows somebody actually filled in count. A name or a positive hour value
 *  is enough to keep the row; a blank row is scaffolding, not scope. */
const realTasks = (tasks: Task[]) => tasks.filter((t) => t.name.trim() || hoursOf(t) > 0);

/** The stored document, back into the shape the form edits. Areas the server
 *  does not know about are dropped; areas it has never heard of open blank. */
function tasksFromAreas(areas: AmSowArea[] | undefined): Record<AreaKey, Task[]> {
  const byKey = new Map((areas ?? []).map((a) => [a.key, a.tasks]));
  return Object.fromEntries(
    AREAS.map((a) => {
      const tasks = (byKey.get(a.key) ?? []).map((t) => ({
        ...blankTask(),
        name: t.name,
        description: t.description,
        /* Hours are text in the form and a number in the document: an input
           the user is halfway through typing into is not a number yet. */
        hours: t.hours ? String(t.hours) : '',
      }));
      return [a.key, tasks.length > 0 ? tasks : [blankTask()]];
    }),
  ) as Record<AreaKey, Task[]>;
}

/* =========================================================================
   The tab: saved scopes, and the builder
   ========================================================================= */

/** The Account management tab: every scope, for every client. */
export function AmSow({ organizationName }: { organizationName: string }) {
  return <SowSection organizationName={organizationName} client={null} />;
}


/**
 * The same thing on a client's own page, filtered to them.
 *
 * Deliberately the same components rather than a read-only summary: a list you
 * cannot open is a list that sends you somewhere else to do the actual work,
 * and the client page is where somebody asks "what have we scoped for them"
 * in the first place. A new scope started here arrives with the client already
 * chosen.
 */
export function AmClientSows({
  clientId,
  organizationName,
}: {
  clientId: string;
  organizationName: string;
}) {
  return <SowSection organizationName={organizationName} client={clientId} />;
}

function SowSection({
  organizationName,
  /** Non-null pins the section to one client: the list is filtered to them and
   *  a new scope starts against them. */
  client,
}: {
  organizationName: string;
  client: string | null;
}) {
  /* Read once here and handed down, because both the list and the builder's
     dropdown want it and it is the same answer. Still read when pinned to one
     client — a saved scope can be re-pointed at a different one. */
  const clients = useResource<AmClientRow[]>('/am/clients');
  const saved = useResource<AmSowRow[]>(
    client ? `/am/sows?clientId=${encodeURIComponent(client)}` : '/am/sows',
  );
  /** null is the list; a string id is that scope; 'new' is a blank one. */
  const [open, setOpen] = useState<string | 'new' | null>(null);
  /** The saved scopes, or the task library they are built from. */
  const [view, setView] = useState<'scopes' | 'library'>('scopes');

  const close = () => {
    setOpen(null);
    saved.reload();
  };

  if (open === 'new') {
    return (
      <SowBuilder
        organizationName={organizationName}
        clients={clients.data ?? []}
        initial={null}
        initialClientId={client ?? ''}
        onClose={close}
      />
    );
  }

  if (open) {
    return (
      <SowLoader
        sowId={open}
        organizationName={organizationName}
        clients={clients.data ?? []}
        onClose={close}
      />
    );
  }

  const list = (
    <SavedScopes
      resource={saved}
      clients={clients.data ?? []}
      pinnedToClient={client !== null}
      onNew={() => setOpen('new')}
      onOpen={setOpen}
    />
  );

  /* On a client's page it is their scopes alone: the library is the whole
     company's, and is tidied from the Scope of work tab. */
  if (client !== null) return list;

  return (
    <>
      {/* The library lives with the scopes it is built into, not on a tab of
          its own. Only on the list — switching away from an open scope would
          throw away whatever had not been saved. */}
      <div className="mb-4">
        <Segmented
          label="Scopes or task library"
          value={view}
          onChange={setView}
          options={[
            { id: 'scopes', label: 'Scopes' },
            { id: 'library', label: 'Task library' },
          ]}
        />
      </div>
      {view === 'library' ? <AmSowTasks /> : list}
    </>
  );
}

/** Loads one saved scope before the builder mounts, so the form's initial
 *  state is the document rather than something an effect fills in later. */
function SowLoader({
  sowId,
  organizationName,
  clients,
  onClose,
}: {
  sowId: string;
  organizationName: string;
  clients: AmClientRow[];
  onClose: () => void;
}) {
  const resource = useResource<AmSowDetail>(`/am/sows/${sowId}`);
  if (resource.loading) return <Loading label="Loading the scope" />;
  if (resource.error) return <ErrorBanner message={resource.error} />;
  if (!resource.data) return null;
  return (
    <SowBuilder
      organizationName={organizationName}
      clients={clients}
      initial={resource.data}
      onClose={onClose}
    />
  );
}

/* =========================================================================
   The list
   ========================================================================= */

function SavedScopes({
  resource,
  clients,
  pinnedToClient,
  onNew,
  onOpen,
}: {
  resource: ReturnType<typeof useResource<AmSowRow[]>>;
  clients: AmClientRow[];
  /** The server already filtered to one client, so the picker and the client
   *  column are both answering a question nobody is asking here. */
  pinnedToClient: boolean;
  onNew: () => void;
  onOpen: (id: string) => void;
}) {
  const [clientId, setClientId] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (resource.loading) return <Loading label="Loading saved scopes" />;
  if (resource.error) return <ErrorBanner message={resource.error} />;
  const scopes = resource.data ?? [];
  const filtered = scopes.filter(
    (s) => (!clientId || s.AmClientId === clientId) && (!status || s.Status === status),
  );

  async function remove(scope: AmSowRow) {
    try {
      await api.del(`/am/sows/${scope.AmSowId}`);
      resource.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete the scope');
    }
  }

  async function changeStatus(scope: AmSowRow, next: AmSowStatus) {
    if (next === scope.Status) return;
    setError(null);
    try {
      await api.put(`/am/sows/${scope.AmSowId}/status`, { status: next });
      resource.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change the status');
    }
  }

  return (
    <>
      <div className="row-between wrap mb-4">
        <div className="row">
          {!pinnedToClient && (
            <select
              className="select"
              style={{ maxWidth: 240 }}
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              aria-label="Filter scopes by client"
            >
              <option value="">Every client</option>
              {clients.map((c) => (
                <option key={c.AmClientId} value={c.AmClientId}>
                  {c.Name}
                </option>
              ))}
            </select>
          )}
          <select
            className="select"
            style={{ maxWidth: 200 }}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            aria-label="Filter scopes by status"
          >
            <option value="">Any status</option>
            {SOW_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <span className="xs muted">
            {filtered.length} of {scopes.length}
          </span>
        </div>
        <button type="button" className="btn btn-primary btn-sm" onClick={onNew}>
          New scope of work
        </button>
      </div>

      {error && <ErrorBanner message={error} />}

      {scopes.length === 0 ? (
        <Empty title="No scopes of work yet">
          <p>
            Build one and it is saved against the client it was written for, so it can be reopened
            and revised rather than re-typed. Five standard work areas, the tasks and hours under
            each, and the products needed to deliver it.
          </p>
        </Empty>
      ) : filtered.length === 0 ? (
        <Empty title="Nothing matches that filter" />
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Scope</th>
                  {!pinnedToClient && <th>Client</th>}
                  <th style={{ width: 170 }}>Status</th>
                  <th className="num" style={{ width: 90 }}>Hours</th>
                  <th className="num" style={{ width: 110 }}>Rate</th>
                  <th className="num" style={{ width: 90 }}>Products</th>
                  <th style={{ width: 120 }}>Updated</th>
                  <th style={{ width: 150 }} />
                </tr>
              </thead>
              <tbody>
                {filtered.map((scope) => (
                  <tr key={scope.AmSowId}>
                    <td>
                      <div className="strong">{scope.Title}</div>
                      {scope.Stakeholder && (
                        <div className="xs muted">for {scope.Stakeholder}</div>
                      )}
                    </td>
                    {!pinnedToClient && (
                      <td>
                        {scope.ClientName}
                        {/* The scope stays listed when the client comes off the
                            books — what was proposed still happened — but the
                            row says so rather than pretending otherwise. */}
                        {!scope.ClientIsActive && <div className="xs muted">deactivated</div>}
                      </td>
                    )}
                    <td>
                      <select
                        className="select"
                        style={{ minWidth: 150, padding: '4px 8px', fontSize: 'var(--text-sm)' }}
                        value={scope.Status}
                        onChange={(e) => void changeStatus(scope, e.target.value as AmSowStatus)}
                        aria-label={`Status of ${scope.Title}`}
                      >
                        {SOW_STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="num">{scope.TotalHours.toFixed(1)}</td>
                    <td className="num">
                      {scope.HourlyRate ? formatNumber(scope.HourlyRate, 'currency') : '—'}
                    </td>
                    <td className="num">{scope.Products || '—'}</td>
                    <td className="xs muted">{formatDate(scope.ModifiedUtc)}</td>
                    <td className="actions">
                      <span className="row">
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => onOpen(scope.AmSowId)}
                        >
                          Open
                        </button>
                        <DeleteButton
                          what={`the scope "${scope.Title}"`}
                          onConfirm={() => remove(scope)}
                        />
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

/* =========================================================================
   The builder
   ========================================================================= */

function SowBuilder({
  organizationName,
  clients,
  initial,
  initialClientId = '',
  onClose,
}: {
  organizationName: string;
  clients: AmClientRow[];
  /** The scope being edited, or null for a new one. */
  initial: AmSowDetail | null;
  /** Pre-chosen client for a new scope, when one was started from a client's
   *  own page. Ignored when `initial` carries its own. */
  initialClientId?: string;
  onClose: () => void;
}) {
  const [step, setStep] = useState(1);
  /** Set once saved, so the next save is an update rather than a second copy. */
  const [sowId, setSowId] = useState<string | null>(initial?.AmSowId ?? null);
  const [clientId, setClientId] = useState(initial?.AmClientId ?? initialClientId);
  /* Only ever read when the dropdown is on OFF_BOOK. A company that is not a
     client yet still gets a printable scope. */
  const [offBookName, setOffBookName] = useState('');
  const [title, setTitle] = useState(initial?.Title ?? 'Scope of work');
  /* A scope being written for the first time is New: it has not been sent to
     anybody, so it is not waiting on an approval yet. */
  const [status, setStatus] = useState<AmSowStatus>(initial?.Status ?? 'New');
  const [details, setDetails] = useState({
    stakeholder: initial?.Stakeholder ?? '',
    purpose: initial?.Purpose ?? '',
    hourlyRate: initial?.HourlyRate != null ? String(initial.HourlyRate) : '',
  });
  const [tasksByArea, setTasksByArea] = useState<Record<AreaKey, Task[]>>(() =>
    tasksFromAreas(initial?.Areas),
  );

  /* What the engineer needs to buy to do the job — licences, appliances, the
     backup unit. Not the same question the recommendation picker asks, which is
     what the CLIENT should buy, but the same catalogue and the same picker. */
  const [products, setProducts] = useState<PickedProduct[]>(() => [
    ...(initial?.Products ?? []).map((p) => ({
      productId: p.ProductId,
      quantity: p.Quantity,
      name: p.Name,
      unitPrice: p.UnitPrice,
      periodType: p.PeriodType,
      vendor: p.Vendor,
    })),
    /* Typed in on the scope, not from the catalogue. A negative id keeps them
       apart from catalogue ids in the list. */
    ...(initial?.CustomProducts ?? []).map((p) => ({
      productId: -p.AmSowCustomProductId,
      custom: true,
      quantity: p.Quantity,
      name: p.Name,
      unitPrice: p.UnitPrice,
      periodType: p.PeriodType,
      vendor: p.Vendor,
    })),
  ]);

  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  /** The name-and-status dialog. Open on the way to a save, never on its own. */
  const [naming, setNaming] = useState(false);
  /** Which area's library picker is open, if any. */
  const [picking, setPicking] = useState<AreaKey | null>(null);
  /** `area:name` of the row whose star was last filled, so the button can say
   *  it worked without a toast for a one-click action. */
  const [savedToLibrary, setSavedToLibrary] = useState<string | null>(null);
  const [savingToLibrary, setSavingToLibrary] = useState<string | null>(null);
  const [libraryError, setLibraryError] = useState<string | null>(null);
  /** The two template dialogs: saving this scope as one, and adding one in. */
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [addingTemplate, setAddingTemplate] = useState(false);
  /** Name of the template last added, so the pill row can say it landed. */
  const [addedTemplate, setAddedTemplate] = useState<string | null>(null);

  const sensors = useSensors(
    /* A few pixels before a drag begins, so a click that lands on the handle
       and a drag that starts there stay distinguishable — the same constraint
       the dashboard's tiles use. */
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  /* A deactivated client is off /am/clients but may well own this scope, so
     the dropdown carries the one it was saved against whatever the list says.
     Without this, opening such a scope would silently reset the client. */
  const options =
    initial && !clients.some((c) => c.AmClientId === initial.AmClientId)
      ? [
          { AmClientId: initial.AmClientId, Name: `${initial.ClientName} (deactivated)` },
          ...clients,
        ]
      : clients;

  const companyName =
    clientId === OFF_BOOK
      ? offBookName
      : (options.find((c) => c.AmClientId === clientId)?.Name ?? '');

  const rate = Number.parseFloat(details.hourlyRate) || 0;
  const totalHours = AREAS.reduce((sum, a) => sum + areaHours(realTasks(tasksByArea[a.key])), 0);
  const labourCost = totalHours * rate;
  const productCost = productTotals(products);
  /* One-off product spend is part of what the project costs. Monthly and annual
     licences are NOT: they are what the client pays from then on, and folding a
     recurring figure into a project total makes the project look several times
     more expensive than it is. They are reported on their own line. */
  const projectCost = labourCost + productCost.oneTime;
  const area = AREAS[step - 2];

  /** A scope is stored against a client, so there is nothing to store it on
   *  until one is picked. Everything else about the builder still works. */
  const canSave = clientId !== '' && clientId !== OFF_BOOK;

  function goTo(next: number) {
    if (next < 1 || next > STEPS.length) return;
    setStep(next);
    /* No scrolling. This used to jump the window to the very top, which is
       ABOVE the step bar — so every step change threw away the one thing the
       person was looking at and made them scroll back down to carry on. The
       bar does not move between steps; leaving the page where it is keeps it
       exactly where it was clicked. */
  }

  function setTask(key: AreaKey, index: number, patch: Partial<Task>) {
    setDirty(true);
    setTasksByArea((prev) => ({
      ...prev,
      [key]: prev[key].map((t, i) => (i === index ? { ...t, ...patch } : t)),
    }));
  }

  /**
   * Drops picked library tasks into an area as ordinary editable rows.
   *
   * They are copies from here on. Adjusting the hours or the wording on the
   * scope is the point — that is the part that varies per engagement — and it
   * does not touch the library.
   */
  function addFromLibrary(key: AreaKey, picked: AmSowLibraryTask[]) {
    if (picked.length === 0) return;
    setDirty(true);
    setTasksByArea((prev) => {
      /* The blank scaffolding row is replaced rather than pushed past, so
         picking three tasks into an untouched area gives three rows and not
         three plus an empty one. */
      const kept = prev[key].filter((t) => t.name.trim() || hoursOf(t) > 0);
      return {
        ...prev,
        [key]: [
          ...kept,
          ...picked.map((t) => ({
            ...blankTask(),
            name: t.Name,
            description: t.Description ?? '',
            hours: t.DefaultHours != null ? String(t.DefaultHours) : '',
          })),
        ],
      };
    });
    setPicking(null);
  }

  /**
   * Moves a task within its area.
   *
   * Local state and nothing else: the order of an area's tasks IS the order of
   * the rows in the printed document, and it is stored as a JSON array on the
   * scope — so dragging a row is an edit to the document like any other and
   * rides along on the next save. Nothing is written here, which is why
   * dropping a row marks the scope dirty rather than calling the API.
   */
  function reorderTasks(key: AreaKey, e: DragEndEvent) {
    const activeId = String(e.active.id);
    const overId = e.over ? String(e.over.id) : null;
    if (!overId || activeId === overId) return;
    setTasksByArea((prev) => {
      const list = prev[key];
      const from = list.findIndex((t) => t.id === activeId);
      const to = list.findIndex((t) => t.id === overId);
      if (from < 0 || to < 0) return prev;
      return { ...prev, [key]: arrayMove(list, from, to) };
    });
    setDirty(true);
  }

  /** Puts a task somebody has typed into the library, or refreshes the entry
   *  already under that name — the server decides which. */
  async function saveToLibrary(key: AreaKey, task: Task) {
    const name = task.name.trim();
    if (!name) return;
    setSavingToLibrary(`${key}:${name}`);
    setLibraryError(null);
    try {
      await api.post('/am/sow-tasks', {
        areaKey: key,
        name,
        description: task.description.trim() || null,
        defaultHours: task.hours.trim() === '' ? null : hoursOf(task),
      });
      setSavedToLibrary(`${key}:${name}`);
    } catch (err) {
      setLibraryError(
        err instanceof Error ? err.message : 'Could not save that task to the library',
      );
    } finally {
      setSavingToLibrary(null);
    }
  }

  /** The work and the products, as both a scope and a template store them. */
  function workPayload() {
    return {
      areas: AREAS.map((a) => ({
        key: a.key,
        tasks: realTasks(tasksByArea[a.key]).map((t) => ({
          name: t.name,
          description: t.description,
          hours: hoursOf(t),
        })),
      })),
      /* Quantity and an id only. The server re-reads the price from the
         catalogue — a price sent from a browser is a price a browser
         chose. */
      products: products.filter((p) => !p.custom).map((p) => ({ productId: p.productId, quantity: p.quantity })),
      /* Not in the catalogue: these carry their own name and price. */
      customProducts: products
        .filter((p) => p.custom && p.name.trim())
        .map((p) => ({ name: p.name.trim(), vendor: p.vendor, quantity: p.quantity, unitPrice: p.unitPrice, periodType: p.periodType })),
    };
  }

  /**
   * Adds a template to what is on screen.
   *
   * Added, not swapped in: two templates — "Firewall swap" and "Wi-Fi refresh"
   * — can make one scope. Its tasks join the end of each area (replacing the
   * blank scaffolding row, as a library pick does), a catalogue product already
   * on the scope has its quantity raised rather than listed twice, and the
   * purpose and rate are only filled where this scope has none of its own.
   */
  function addTemplate(template: AmSowTemplateDetail) {
    const incoming = tasksFromAreas(template.Areas);
    setTasksByArea(
      (prev) =>
        Object.fromEntries(
          AREAS.map((a) => {
            const added = realTasks(incoming[a.key]);
            if (added.length === 0) return [a.key, prev[a.key]];
            return [a.key, [...realTasks(prev[a.key]), ...added]];
          }),
        ) as Record<AreaKey, Task[]>,
    );
    setProducts((prev) => {
      const next = [...prev];
      for (const p of template.Products) {
        const at = next.findIndex((n) => !n.custom && n.productId === p.ProductId);
        const existing = next[at];
        if (existing) {
          next[at] = { ...existing, quantity: existing.quantity + Number(p.Quantity) };
        } else {
          next.push({
            productId: p.ProductId,
            quantity: Number(p.Quantity),
            name: p.Name,
            unitPrice: p.UnitPrice,
            periodType: p.PeriodType,
            vendor: p.Vendor,
          });
        }
      }
      /* Fresh negative ids below anything on screen, so a template's typed-in
         products cannot share a key with the scope's own. */
      let lowest = Math.min(0, ...next.map((n) => n.productId));
      for (const p of template.CustomProducts) {
        next.push({
          productId: --lowest,
          custom: true,
          quantity: Number(p.Quantity),
          name: p.Name,
          unitPrice: p.UnitPrice,
          periodType: p.PeriodType,
          vendor: p.Vendor,
        });
      }
      return next;
    });
    setDetails((prev) => ({
      ...prev,
      purpose: prev.purpose.trim() ? prev.purpose : (template.Purpose ?? ''),
      hourlyRate:
        prev.hourlyRate.trim() || template.HourlyRate == null
          ? prev.hourlyRate
          : String(template.HourlyRate),
    }));
    setDirty(true);
    setAddedTemplate(template.Name);
    setAddingTemplate(false);
  }

  /**
   * Writes the scope.
   *
   * `title` and `status` are sent on EVERY save, not only from the dialog:
   * they are ordinary fields of the document by the time it is saved once, and
   * omitting them would make "set it to Approved, then fix a typo in a task"
   * quietly drop the approval on the second save.
   */
  async function save(override?: { status: AmSowStatus }): Promise<boolean> {
    if (!canSave) return false;
    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        clientId,
        title: title.trim() || 'Scope of work',
        status: override?.status ?? status,
        stakeholder: details.stakeholder,
        purpose: details.purpose,
        /* An empty rate is no rate, not a rate of zero: a scope without one is
           a scope of work rather than a quote, which is a legitimate thing to
           write. */
        hourlyRate: details.hourlyRate.trim() === '' ? null : rate,
        ...workPayload(),
      };
      if (sowId) {
        await api.put(`/am/sows/${sowId}`, payload);
      } else {
        const created = await api.post<{ AmSowId: string }>('/am/sows', payload);
        setSowId(created.AmSowId);
      }
      setDirty(false);
      setNaming(false);
      return true;
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Could not save the scope');
      return false;
    } finally {
      setSaving(false);
    }
  }

  /** Saves what is on screen and marks it Approved in the same write. The
   *  badge only changes once the server has agreed, so a failed save does not
   *  leave the page claiming an approval that never happened. */
  async function approve() {
    if (await save({ status: 'Approved' })) setStatus('Approved');
  }

  /**
   * Save from the header.
   *
   * A scope that has never been saved has no name and no status yet, so it
   * goes through the dialog; one that has is written in place. The alternative
   * — a dialog on every save — puts two clicks and a confirmation between
   * somebody and keeping the paragraph they just typed.
   */
  function saveFromHeader() {
    if (sowId) void save();
    else setNaming(true);
  }

  function close() {
    if (dirty && !window.confirm('Close without saving? The changes since the last save are lost.'))
      return;
    onClose();
  }

  function exportCsv() {
    const rows: unknown[][] = [
      ['Scope of work', companyName],
      ['Prepared for', details.stakeholder],
      ['Date', formatDate(new Date().toISOString())],
      [],
      ['Area', 'Task', 'Description', 'Hours'],
    ];
    for (const a of AREAS) {
      for (const task of realTasks(tasksByArea[a.key])) {
        rows.push([a.name, task.name.trim() || 'Untitled task', task.description, hoursOf(task)]);
      }
    }
    if (products.length > 0) {
      rows.push([], ['Products required', 'Vendor', 'Quantity', 'Unit price', 'Bills', 'Line total']);
      for (const item of products) {
        rows.push([
          item.name,
          item.vendor ?? '',
          item.quantity,
          item.unitPrice,
          item.periodType,
          item.unitPrice * item.quantity,
        ]);
      }
    }
    rows.push([], ['', '', 'Total hours', totalHours]);
    if (rate > 0) rows.push(['', '', 'Estimated labour', labourCost]);
    if (productCost.oneTime !== 0) rows.push(['', '', 'One-off products', productCost.oneTime]);
    if (rate > 0 || productCost.oneTime !== 0) rows.push(['', '', 'Project cost', projectCost]);
    if (productCost.monthly !== 0) rows.push(['', '', 'Recurring monthly', productCost.monthly]);
    if (productCost.annual !== 0) rows.push(['', '', 'Recurring annually', productCost.annual]);
    downloadCsv(`sow-${slugForFile(companyName || 'client')}-${fileStamp()}.csv`, rows);
  }

  return (
    <>
      {/* Save lives up here rather than only on the last step: a scope takes a
          sitting to write, and "finish the wizard before you may keep any of
          it" is how an hour of somebody's work gets lost to a closed tab. */}
      <div className="row-between wrap mb-4 no-print">
        <button type="button" className="btn btn-secondary btn-sm" onClick={close}>
          ← Saved scopes
        </button>
        <span className="row">
          {sowId && <StateBadge status={status} />}
          {!canSave && (
            <span className="xs muted">Pick a client to save this against</span>
          )}
          {canSave && dirty && <span className="xs muted">Unsaved changes</span>}
          {canSave && !dirty && sowId && <span className="xs muted">Saved</span>}
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={!canSave || saving}
            onClick={saveFromHeader}
          >
            {saving ? 'Saving…' : sowId ? 'Save changes' : 'Save scope…'}
          </button>
        </span>
      </div>

      {saveError && <ErrorBanner message={saveError} />}

      {/* Templates sit over the step bar's far end, by Review: saving one is
          what you do with a finished scope, and adding one belongs to no
          single work area. */}
      <div className="am-template-pills no-print">
        {addedTemplate && <span className="xs muted">Added "{addedTemplate}"</span>}
        <button type="button" className="am-step" onClick={() => setAddingTemplate(true)}>
          <span className="n">+</span>
          Add a template
        </button>
        <button type="button" className="am-step" onClick={() => setSavingTemplate(true)}>
          <span className="n">★</span>
          Save as template
        </button>
      </div>

      <div className="am-steps no-print">
        {STEPS.map((label, i) => {
          const n = i + 1;
          return (
            <Fragment key={label}>
              <button
                type="button"
                className={`am-step${n === step ? ' active' : n < step ? ' done' : ''}`}
                onClick={() => goTo(n)}
              >
                <span className="n">{n}</span>
                {label}
              </button>
            </Fragment>
          );
        })}
      </div>

      <div className="no-print">
        {step === 1 && (
          <section className="card">
            <div className="card-head">
              <div className="grow">
                <span className="eyebrow">Step one</span>
                <h2>Project details</h2>
                <p className="sub">The basics that head the document.</p>
              </div>
            </div>
            <div className="card-body">
              <Field
                label="Company"
                hint="The client this scope is for. Saving files it against them, so it can be reopened and revised."
              >
                <select
                  className="select"
                  value={clientId}
                  onChange={(e) => {
                    setDirty(true);
                    setClientId(e.target.value);
                  }}
                >
                  <option value="">Choose a client…</option>
                  {options.map((c) => (
                    <option key={c.AmClientId} value={c.AmClientId}>
                      {c.Name}
                    </option>
                  ))}
                  <option value={OFF_BOOK}>Someone else — type a name</option>
                </select>
              </Field>
              {clientId === OFF_BOOK && (
                <Field
                  label="Company name"
                  hint="Printable, but not saved: a scope is stored against a client. Add them as a client first to keep this one."
                >
                  <input
                    className="input"
                    placeholder="e.g. Northwind Traders"
                    value={offBookName}
                    onChange={(e) => setOffBookName(e.target.value)}
                  />
                </Field>
              )}
              <Field label="Scope title" hint="How this one is listed. e.g. Firewall replacement.">
                <input
                  className="input"
                  placeholder="Scope of work"
                  value={title}
                  onChange={(e) => {
                    setDirty(true);
                    setTitle(e.target.value);
                  }}
                />
              </Field>
              <Field label="Stakeholder">
                <input
                  className="input"
                  placeholder="e.g. Jane Smith, VP of Operations"
                  value={details.stakeholder}
                  onChange={(e) => {
                    setDirty(true);
                    setDetails({ ...details, stakeholder: e.target.value });
                  }}
                />
              </Field>
              <Field label="Purpose of the scope">
                <textarea
                  className="textarea"
                  rows={4}
                  placeholder="What this engagement is for…"
                  value={details.purpose}
                  onChange={(e) => {
                    setDirty(true);
                    setDetails({ ...details, purpose: e.target.value });
                  }}
                />
              </Field>
              <Field label="Hourly rate" hint="Optional. Only used for the cost estimate.">
                <input
                  className="input"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="e.g. 150"
                  value={details.hourlyRate}
                  onChange={(e) => {
                    setDirty(true);
                    setDetails({ ...details, hourlyRate: e.target.value });
                  }}
                />
              </Field>
              <div className="row-between mt-4">
                <span />
                <button type="button" className="btn btn-primary" onClick={() => goTo(2)}>
                  Next →
                </button>
              </div>
            </div>
          </section>
        )}

        {area && (
          <section className="card">
            <div className="card-head">
              <div className="grow">
                <span className="eyebrow">Work area</span>
                <h2>{area.name}</h2>
                <p className="sub">Tasks and estimated hours for {area.name.toLowerCase()}.</p>
              </div>
            </div>
            <div className="card-body">
              <Field label="Standard description" hint="Fixed wording — it lands in the document as-is.">
                <div className="am-boilerplate">{area.description}</div>
              </Field>

              <div className="am-task-head mt-4">
                <span />
                <span className="xs muted">Task</span>
                <span className="xs muted">Description</span>
                <span className="xs muted">Hours</span>
                <span />
                <span />
              </div>
              {/* closestCorners, and the reason is the KEYBOARD.

                  Pointer-based detection is wrong here either way: a task row
                  is tall (it holds a two-line textarea) and pointer collision
                  only registers a swap once the cursor is inside the next row,
                  which on rows this size means dragging well past the gap you
                  are aiming for. That ruled out pointerWithin.

                  It was closestCenter until keyboard reordering was actually
                  tested with real key events rather than synthetic ones. Space
                  lifted the row and every arrow press then resolved back to the
                  row itself — "moved over droppable area task-13" when task-13
                  was what had been picked up — so the lift worked and nothing
                  could ever move. The keyboard coordinate getter hands
                  collision detection a point on the neighbour, and on rows this
                  tall closestCenter kept resolving that point to the dragged
                  row's own centre. closestCorners resolves it to the neighbour.

                  Both paths verified after the change: arrows move the row and
                  Space drops it, and a simulated pointer drag still moves a row
                  from first to third. */}
              <DndContext
                sensors={sensors}
                collisionDetection={closestCorners}
                onDragEnd={(e) => reorderTasks(area.key, e)}
              >
                <SortableContext
                  items={tasksByArea[area.key].map((t) => t.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {tasksByArea[area.key].map((task, i) => (
                    <SortableTaskRow
                      key={task.id}
                      task={task}
                      areaName={area.name}
                      /* The last row cannot be removed — an area with no rows
                         looks broken rather than empty — and a list of one has
                         nothing to reorder, so the handle goes too. */
                      canRemove={tasksByArea[area.key].length > 1}
                      canReorder={tasksByArea[area.key].length > 1}
                      starred={savedToLibrary === `${area.key}:${task.name.trim()}`}
                      savingToLibrary={savingToLibrary !== null}
                      onChange={(patch) => setTask(area.key, i, patch)}
                      onRemove={() => {
                        setDirty(true);
                        setTasksByArea((prev) => ({
                          ...prev,
                          [area.key]: prev[area.key].filter((_, n) => n !== i),
                        }));
                      }}
                      onStar={() => void saveToLibrary(area.key, task)}
                    />
                  ))}
                </SortableContext>
              </DndContext>
              <span className="row">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setPicking(area.key)}
                >
                  + Add from the library
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    setDirty(true);
                    setTasksByArea((prev) => ({
                      ...prev,
                      [area.key]: [...prev[area.key], blankTask()],
                    }));
                  }}
                >
                  + Add a blank task
                </button>
                {libraryError && <span className="xs accent">{libraryError}</span>}
              </span>

              <div className="row-between mt-6">
                <button type="button" className="btn btn-secondary" onClick={() => goTo(step - 1)}>
                  ← Back
                </button>
                <span className="xs muted">
                  {formatNumber(areaHours(realTasks(tasksByArea[area.key])), 'hours')} in this area
                </span>
                <button type="button" className="btn btn-primary" onClick={() => goTo(step + 1)}>
                  Next →
                </button>
              </div>
            </div>
          </section>
        )}

        {step === PRODUCTS_STEP && (
          <section className="card">
            <div className="card-head">
              <div className="grow">
                <span className="eyebrow">Nearly there</span>
                <h2>Products required</h2>
                <p className="sub">
                  What has to be bought to deliver this — licences, appliances, the hardware going
                  on site. Priced from the catalogue at list, or typed in for anything the catalogue does not carry.
                </p>
              </div>
            </div>
            <div className="card-body">
              <AmProductPicker
                items={products}
                onChange={(next) => {
                  setDirty(true);
                  setProducts(next);
                }}
                label="Products required"
                hint="Search the price book, or add a product it does not carry with its own price. Quantities are per the project, not per seat per month."
                allowCustom
              />

              {products.length > 0 && (
                <div className="grid grid-3 mt-4">
                  <div className="stat">
                    <div className="label">One-off</div>
                    <div className="figure">{formatNumber(productCost.oneTime, 'currency')}</div>
                    <div className="foot">part of the project cost</div>
                  </div>
                  <div className="stat">
                    <div className="label">Monthly</div>
                    <div className="figure">{formatNumber(productCost.monthly, 'currency')}</div>
                    <div className="foot">recurring, not in the project total</div>
                  </div>
                  <div className="stat">
                    <div className="label">Annual</div>
                    <div className="figure">{formatNumber(productCost.annual, 'currency')}</div>
                    <div className="foot">recurring, not in the project total</div>
                  </div>
                </div>
              )}

              <div className="row-between mt-6">
                <button type="button" className="btn btn-secondary" onClick={() => goTo(step - 1)}>
                  ← Back
                </button>
                <span className="xs muted">
                  {products.length === 0
                    ? 'Nothing needed — skip it'
                    : `${products.length} ${products.length === 1 ? 'product' : 'products'}`}
                </span>
                <button type="button" className="btn btn-primary" onClick={() => goTo(step + 1)}>
                  Next →
                </button>
              </div>
            </div>
          </section>
        )}

        {step === STEPS.length && (
          <section className="card mb-4">
            <div className="card-head">
              <div className="grow">
                <span className="eyebrow">Last step</span>
                <h2>Review and export</h2>
                <p className="sub">
                  Check the totals, then save it against the client, print to PDF, or take the
                  numbers away as a spreadsheet.
                </p>
              </div>
            </div>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Area</th>
                    <th style={{ width: 120 }}>Tasks</th>
                    <th className="num" style={{ width: 100 }}>Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {AREAS.map((a) => {
                    const tasks = realTasks(tasksByArea[a.key]);
                    return (
                      <tr key={a.key}>
                        <td className="strong">{a.name}</td>
                        <td className="xs muted">
                          {tasks.length} task{tasks.length === 1 ? '' : 's'}
                        </td>
                        <td className="num">{areaHours(tasks).toFixed(1)}</td>
                      </tr>
                    );
                  })}
                  <tr>
                    <td colSpan={2} className="strong">
                      Total hours
                    </td>
                    <td className="num strong">{totalHours.toFixed(1)}</td>
                  </tr>
                  {rate > 0 && (
                    <tr>
                      <td colSpan={2} className="strong">
                        Labour at {formatNumber(rate, 'currency')} an hour
                      </td>
                      <td className="num strong">{formatNumber(labourCost, 'currency')}</td>
                    </tr>
                  )}
                  {products.length > 0 && (
                    <tr>
                      <td className="strong">Products</td>
                      <td className="xs muted">
                        {products.length} {products.length === 1 ? 'product' : 'products'}
                      </td>
                      {/* Only the one-off part belongs in a project total.
                          A dash rather than $0 when everything picked is a
                          licence: "$0 of products" reads as a mistake. */}
                      <td className="num strong">
                        {productCost.oneTime === 0
                          ? '—'
                          : formatNumber(productCost.oneTime, 'currency')}
                      </td>
                    </tr>
                  )}
                  {(rate > 0 || productCost.oneTime !== 0) && (
                    <tr>
                      <td colSpan={2} className="strong">
                        Project cost
                      </td>
                      <td className="num strong">{formatNumber(projectCost, 'currency')}</td>
                    </tr>
                  )}
                  {(productCost.monthly !== 0 || productCost.annual !== 0) && (
                    <tr>
                      <td colSpan={2} className="strong">
                        Recurring afterwards
                      </td>
                      <td className="num strong">
                        {[
                          productCost.monthly !== 0 &&
                            `${formatNumber(productCost.monthly, 'currency')}/mo`,
                          productCost.annual !== 0 &&
                            `${formatNumber(productCost.annual, 'currency')}/yr`,
                        ]
                          .filter(Boolean)
                          .join(' + ')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="card-body row-between wrap">
              <button type="button" className="btn btn-secondary" onClick={() => goTo(STEPS.length - 1)}>
                ← Back
              </button>
              <span className="row">
                <button type="button" className="btn btn-secondary" onClick={exportCsv}>
                  Export CSV
                </button>
                {/* Always the dialog here, unlike the header. This is the end
                    of the wizard — the moment somebody decides what the scope
                    is called and whether it is going out for approval — and
                    the one place where being asked is the point rather than an
                    interruption. */}
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={!canSave || saving}
                  onClick={() => setNaming(true)}
                >
                  Save scope…
                </button>
                {/* Anything not already settled can be approved from here —
                    a scope often goes out and comes back agreed without
                    anybody touching the dropdown in between. */}
                {(status === 'New' || status === 'Needs approval') && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={!canSave || saving}
                    onClick={() => void approve()}
                  >
                    Approve
                  </button>
                )}
                <button type="button" className="btn btn-primary" onClick={() => window.print()}>
                  Print / save PDF
                </button>
              </span>
            </div>
          </section>
        )}
      </div>

      {/* ---------- the printable document ---------- */}
      <article className="am-doc">
        <header className="am-doc-head">
          <div>
            {/* Letterhead. The document leaves the building — it is printed,
                saved as a PDF and sent to the client — and arriving with the
                organisation's name set in small caps and nothing else looked
                like a draft. The name stays under it: the logo is the firm,
                the line is which office. */}
            <img className="am-doc-logo" src={cmitLogo} alt="CMIT Solutions" />
            <div className="am-doc-brand">{organizationName}</div>
            <h2>Scope of work</h2>
          </div>
          <div className="am-doc-meta">
            <div>
              <strong>Prepared for:</strong> {details.stakeholder || '—'}
            </div>
            <div>
              <strong>Company:</strong> {companyName || '—'}
            </div>
            <div>
              <strong>Date:</strong> {formatDate(new Date().toISOString())}
            </div>
            <div>
              <strong>Estimated hours:</strong> {totalHours.toFixed(1)}
            </div>
          </div>
        </header>

        {details.purpose.trim() && (
          <>
            <div className="am-doc-title">Purpose and objective</div>
            <p className="am-doc-text">{details.purpose}</p>
          </>
        )}

        {AREAS.map((a) => {
          const tasks = realTasks(tasksByArea[a.key]);
          if (tasks.length === 0) return null;
          return (
            <div key={a.key}>
              <div className="am-doc-title">{a.name}</div>
              <p className="muted">{a.description}</p>
              <table className="am-doc-table">
                <thead>
                  <tr>
                    <th style={{ width: '32%' }}>Task</th>
                    <th>Description</th>
                    <th className="num" style={{ width: 90 }}>Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.map((task, i) => (
                    <tr key={i}>
                      <td className="strong">{task.name.trim() || 'Untitled task'}</td>
                      <td className="am-doc-text">{task.description}</td>
                      <td className="num">{hoursOf(task).toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={2} className="num strong">
                      Subtotal
                    </td>
                    <td className="num strong">{areaHours(tasks).toFixed(1)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          );
        })}

        {products.length > 0 && (
          <div>
            <div className="am-doc-title">Products required</div>
            <p className="muted">
              The licences, hardware and services required to deliver this scope, priced at list.
              One-off items are included in the project cost below; anything that bills monthly or
              annually continues after the project closes and is shown separately.
            </p>
            <table className="am-doc-table">
              <thead>
                <tr>
                  <th style={{ width: '40%' }}>Product</th>
                  <th>Vendor</th>
                  <th className="num" style={{ width: 60 }}>Qty</th>
                  <th className="num" style={{ width: 90 }}>Unit</th>
                  <th style={{ width: 80 }}>Bills</th>
                  <th className="num" style={{ width: 90 }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {products.map((item) => (
                  <tr key={item.productId}>
                    <td className="strong">{item.name}</td>
                    <td>{item.vendor ?? '—'}</td>
                    <td className="num">{item.quantity}</td>
                    <td className="num">{formatNumber(item.unitPrice, 'currency')}</td>
                    <td>{item.periodType}</td>
                    <td className="num">
                      {formatNumber(item.unitPrice * item.quantity, 'currency')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="am-doc-title">Total</div>
        <table className="am-doc-table">
          <tbody>
            <tr>
              <td className="strong">Total estimated hours</td>
              <td className="num strong">{totalHours.toFixed(1)}</td>
            </tr>
            {rate > 0 && (
              <tr>
                <td className="strong">
                  Labour at {formatNumber(rate, 'currency')} an hour
                </td>
                <td className="num strong">{formatNumber(labourCost, 'currency')}</td>
              </tr>
            )}
            {productCost.oneTime !== 0 && (
              <tr>
                <td className="strong">One-off products</td>
                <td className="num strong">{formatNumber(productCost.oneTime, 'currency')}</td>
              </tr>
            )}
            {(rate > 0 || productCost.oneTime !== 0) && (
              <tr>
                <td className="strong">Estimated project cost</td>
                <td className="num strong">{formatNumber(projectCost, 'currency')}</td>
              </tr>
            )}
            {productCost.monthly !== 0 && (
              <tr>
                <td className="strong">Recurring monthly, after go-live</td>
                <td className="num strong">{formatNumber(productCost.monthly, 'currency')}</td>
              </tr>
            )}
            {productCost.annual !== 0 && (
              <tr>
                <td className="strong">Recurring annually, after go-live</td>
                <td className="num strong">{formatNumber(productCost.annual, 'currency')}</td>
              </tr>
            )}
          </tbody>
        </table>
      </article>

      {picking && (
        <SowLibraryPicker
          areaKey={picking}
          areaName={AREAS.find((a) => a.key === picking)?.name ?? picking}
          onClose={() => setPicking(null)}
          onAdd={(tasks) => addFromLibrary(picking, tasks)}
        />
      )}

      {savingTemplate && (
        <SaveTemplateDialog
          initialName={title.trim() && title.trim() !== 'Scope of work' ? title.trim() : ''}
          summary={`${totalHours.toFixed(1)} hours across ${AREAS.filter((a) => realTasks(tasksByArea[a.key]).length > 0).length} work areas, ${products.length} ${products.length === 1 ? 'product' : 'products'}.`}
          onClose={() => setSavingTemplate(false)}
          onSave={(name) =>
            api.post('/am/sow-templates', {
              name,
              purpose: details.purpose,
              hourlyRate: details.hourlyRate.trim() === '' ? null : rate,
              ...workPayload(),
            })
          }
        />
      )}

      {addingTemplate && (
        <SowTemplatePicker onClose={() => setAddingTemplate(false)} onAdd={addTemplate} />
      )}

      {naming && (
        <Modal
          title={sowId ? 'Save changes' : 'Save this scope'}
          onClose={() => setNaming(false)}
          footer={
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setNaming(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={saving}
                onClick={() => void save()}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </>
          }
        >
          <Field
            label="Name"
            hint="How this scope is listed. e.g. Firewall replacement, Office move — phase 1."
          >
            <input
              className="input"
              autoFocus
              placeholder="Scope of work"
              value={title}
              onChange={(e) => {
                setDirty(true);
                setTitle(e.target.value);
              }}
            />
          </Field>
          <Field
            label="Status"
            hint="Where it has got to with the client. A scope starts as New — it has been written, not sent. Mark it Needs approval once it goes to the client."
          >
            <select
              className="select"
              value={status}
              onChange={(e) => {
                setDirty(true);
                setStatus(e.target.value as AmSowStatus);
              }}
            >
              {SOW_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
          <p className="xs muted">
            Saved against <strong>{companyName || 'nobody yet'}</strong> —{' '}
            {totalHours.toFixed(1)} hours across {AREAS.filter((a) => realTasks(tasksByArea[a.key]).length > 0).length} work
            {' '}areas, {products.length} {products.length === 1 ? 'product' : 'products'}.
          </p>
          {saveError && <ErrorBanner message={saveError} />}
        </Modal>
      )}
    </>
  );
}

/* =========================================================================
   Templates
   ========================================================================= */

/**
 * Names a template and saves it. The client, stakeholder, title and status
 * stay behind: a template is the work, not who it was for.
 */
function SaveTemplateDialog({
  initialName,
  summary,
  onClose,
  onSave,
}: {
  initialName: string;
  summary: string;
  onClose: () => void;
  onSave: (name: string) => Promise<unknown>;
}) {
  const [name, setName] = useState(initialName);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!name.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(name.trim());
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the template');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      title="Save as template"
      onClose={onClose}
      footer={
        saved ? (
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        ) : (
          <>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={saving || !name.trim()}
              onClick={() => void submit()}
            >
              {saving ? 'Saving…' : 'Save template'}
            </button>
          </>
        )
      }
    >
      {saved ? (
        <p>
          Saved <strong>{name.trim()}</strong>. Add it to any scope from <em>Add a template</em>.
        </p>
      ) : (
        <>
          <Field
            label="Template name"
            hint="Saving under a name that already exists replaces that template."
          >
            <input
              className="input"
              autoFocus
              placeholder="e.g. Firewall replacement"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void submit();
              }}
            />
          </Field>
          <p className="xs muted">
            Takes the tasks, hours, products, purpose and rate — {summary} The client and
            stakeholder are not saved with it.
          </p>
        </>
      )}
      {error && <ErrorBanner message={error} />}
    </Modal>
  );
}

/** Lists the saved templates; picking one adds it to the scope on screen. */
function SowTemplatePicker({
  onClose,
  onAdd,
}: {
  onClose: () => void;
  onAdd: (template: AmSowTemplateDetail) => void;
}) {
  const templates = useResource<AmSowTemplateRow[]>('/am/sow-templates');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function add(id: string) {
    setBusy(id);
    setError(null);
    try {
      onAdd(await api.get<AmSowTemplateDetail>(`/am/sow-templates/${id}`));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load that template');
      setBusy(null);
    }
  }

  async function remove(id: string) {
    setError(null);
    try {
      await api.del(`/am/sow-templates/${id}`);
      templates.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete that template');
    }
  }

  return (
    <Modal
      title="Add a template"
      onClose={onClose}
      footer={
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          Cancel
        </button>
      }
    >
      <p className="sm muted mb-4">
        Its tasks are added to the end of each work area and its products to the list. Nothing
        already on this scope is removed.
      </p>
      {error && <ErrorBanner message={error} />}
      {templates.loading && <Loading label="Loading templates" />}
      {templates.error && <ErrorBanner message={templates.error} />}
      {templates.data && templates.data.length === 0 && (
        <Empty title="No templates yet">
          <p className="sm muted">Build a scope, then use Save as template to keep it here.</p>
        </Empty>
      )}
      {templates.data && templates.data.length > 0 && (
        <div className="am-template-list">
          {templates.data.map((t) => (
            <div key={t.AmSowTemplateId} className="am-template-item">
              <div className="grow">
                <div className="strong">{t.Name}</div>
                <div className="xs muted">
                  {formatNumber(t.TotalHours)} hours · {t.ProductCount}{' '}
                  {t.ProductCount === 1 ? 'product' : 'products'}
                  {t.CreatedBy ? ` · saved by ${t.CreatedBy}` : ''} · {formatDate(t.ModifiedUtc)}
                </div>
              </div>
              <DeleteButton
                what={`the template "${t.Name}"`}
                consequence="Scopes it was added to keep their tasks."
                onConfirm={() => void remove(t.AmSowTemplateId)}
              />
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={busy !== null}
                onClick={() => void add(t.AmSowTemplateId)}
              >
                {busy === t.AmSowTemplateId ? 'Adding…' : 'Add'}
              </button>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

/* =========================================================================
   The library picker
   ========================================================================= */

/**
 * Picks tasks out of the library into a work area.
 *
 * Opens on that area's own tasks, because that is what somebody standing in
 * the Remote setup step is asking for. The other areas are one checkbox away
 * rather than hidden: "Site survey" is filed under Onsite setup and is
 * occasionally exactly what a remote engagement needs, and a picker that
 * refuses to show it teaches people to type it again instead.
 */
function SowLibraryPicker({
  areaKey,
  areaName,
  onClose,
  onAdd,
}: {
  areaKey: string;
  areaName: string;
  onClose: () => void;
  onAdd: (tasks: AmSowLibraryTask[]) => void;
}) {
  const library = useResource<AmSowLibraryTask[]>('/am/sow-tasks');
  const [everyArea, setEveryArea] = useState(false);
  const [term, setTerm] = useState('');
  const [picked, setPicked] = useState<string[]>([]);

  const all = library.data ?? [];
  const q = term.trim().toLowerCase();
  const shown = all.filter((t) => {
    if (!everyArea && t.AreaKey !== areaKey) return false;
    if (!q) return true;
    return [t.Name, t.Description].filter(Boolean).some((v) => v!.toLowerCase().includes(q));
  });

  /* Picked order, not list order: somebody ticking three tasks is choosing the
     order they land on the document. */
  const chosen = picked
    .map((id) => all.find((t) => t.AmSowTaskId === id))
    .filter((t): t is AmSowLibraryTask => Boolean(t));

  const toggle = (id: string) =>
    setPicked((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));

  const hours = chosen.reduce((sum, t) => sum + (t.DefaultHours ?? 0), 0);

  return (
    <Modal
      title={`Add tasks — ${areaName}`}
      onClose={onClose}
      footer={
        <>
          <span className="xs muted grow">
            {chosen.length === 0
              ? 'Nothing picked yet'
              : `${chosen.length} picked · ${hours.toFixed(1)} hours`}
          </span>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={chosen.length === 0}
            onClick={() => onAdd(chosen)}
          >
            Add {chosen.length > 0 ? chosen.length : ''}
          </button>
        </>
      }
    >
      <div className="row-between wrap mb-4">
        <input
          className="input"
          style={{ maxWidth: 280 }}
          autoFocus
          placeholder="Search the library…"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          aria-label="Search the task library"
        />
        <label className="row xs">
          <input
            type="checkbox"
            checked={everyArea}
            onChange={(e) => setEveryArea(e.target.checked)}
          />
          Show every work area
        </label>
      </div>

      {library.loading && <Loading label="Loading the library" />}
      {library.error && <ErrorBanner message={library.error} />}

      {!library.loading &&
        (shown.length === 0 ? (
          <Empty title={q ? 'Nothing matches that' : 'No tasks in this area yet'}>
            <p>
              Build a task on the scope and press ☆ to keep it, or add entries on the Task library
              tab in Account management.
            </p>
          </Empty>
        ) : (
          <div className="stack">
            {shown.map((task) => {
              const on = picked.includes(task.AmSowTaskId);
              return (
                <label
                  key={task.AmSowTaskId}
                  className={`am-product-line${on ? ' picked' : ''}`}
                  style={{ cursor: 'pointer' }}
                >
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => toggle(task.AmSowTaskId)}
                    aria-label={`Add ${task.Name}`}
                  />
                  <div className="grow">
                    <div className="small strong">{task.Name}</div>
                    {task.Description && <div className="xs subtle">{task.Description}</div>}
                    {/* Only when it came from another area, because inside the
                        area it is the answer to a question nobody asked. */}
                    {everyArea && task.AreaKey !== areaKey && (
                      <div className="xs muted">
                        {AREAS.find((a) => a.key === task.AreaKey)?.name ?? task.AreaKey}
                      </div>
                    )}
                  </div>
                  <span className="mono small">
                    {task.DefaultHours != null ? `${task.DefaultHours.toFixed(1)} hrs` : '—'}
                  </span>
                </label>
              );
            })}
          </div>
        ))}
    </Modal>
  );
}

/* =========================================================================
   One task row
   ========================================================================= */

/**
 * A task on the scope, draggable by its grip.
 *
 * ONLY THE GRIP STARTS A DRAG — `setActivatorNodeRef` is what separates the
 * two. Putting the listeners on the row itself would fight the three form
 * controls inside it: dnd-kit swallows the pointer down it is watching, and
 * selecting a word inside the description would start carrying the row around
 * instead. The dashboard's tiles separate them for the same reason.
 *
 * The keyboard sensor makes the grip a real control rather than a decoration:
 * focus it, space to lift, arrows to move, space to drop, escape to abandon.
 * That matters more here than on the dashboard — this is a form somebody tabs
 * through — which is why the grip is a button and not a styled span.
 */
function SortableTaskRow({
  task,
  areaName,
  canRemove,
  canReorder,
  starred,
  savingToLibrary,
  onChange,
  onRemove,
  onStar,
}: {
  task: Task;
  areaName: string;
  canRemove: boolean;
  canReorder: boolean;
  starred: boolean;
  savingToLibrary: boolean;
  onChange: (patch: Partial<Task>) => void;
  onRemove: () => void;
  onStar: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id, disabled: !canReorder });

  const label = task.name.trim() || 'this untitled task';

  return (
    <div
      ref={setNodeRef}
      className={`am-task-row${isDragging ? ' dragging' : ''}`}
      style={
        {
          transform: CSS.Transform.toString(transform),
          transition,
        } as CSSProperties
      }
    >
      <button
        type="button"
        className="am-task-drag"
        ref={setActivatorNodeRef}
        disabled={!canReorder}
        title={canReorder ? 'Drag to reorder' : 'Nothing to reorder yet'}
        aria-label={`Reorder ${label}`}
        {...attributes}
        {...listeners}
      >
        ⠿
      </button>
      <input
        className="input"
        placeholder="Task name"
        value={task.name}
        onChange={(e) => onChange({ name: e.target.value })}
        aria-label="Task name"
      />
      <textarea
        className="textarea"
        rows={2}
        placeholder="Brief description"
        value={task.description}
        onChange={(e) => onChange({ description: e.target.value })}
        aria-label="Task description"
      />
      <input
        className="input"
        type="number"
        min="0"
        step="0.25"
        placeholder="Hrs"
        value={task.hours}
        onChange={(e) => onChange({ hours: e.target.value })}
        aria-label="Hours"
      />
      {/* How the library actually fills up. Maintaining it only on its own tab
          would mean re-typing a task somebody has just written, which is the
          work this whole feature exists to remove. */}
      <button
        type="button"
        className="btn btn-ghost btn-icon btn-sm"
        title={
          task.name.trim()
            ? `Save "${task.name.trim()}" to the ${areaName.toLowerCase()} library`
            : 'Name the task first to save it to the library'
        }
        aria-label="Save this task to the library"
        disabled={!task.name.trim() || savingToLibrary}
        onClick={onStar}
      >
        {starred ? '★' : '☆'}
      </button>
      <button
        type="button"
        className="btn btn-ghost btn-icon btn-sm"
        title="Remove this task"
        disabled={!canRemove}
        onClick={onRemove}
      >
        ✕
      </button>
    </div>
  );
}
